'use client';

import React from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Calendar, 
  Users, 
  Tag, 
  Coins, 
  Bell 
} from 'lucide-react';

interface SalesHeaderProps {
  salesCount: number;
  currentDateStr: string;
  selectedRange: 'day' | 'week' | 'month' | 'all';
  onRangeChange: (range: 'day' | 'week' | 'month' | 'all') => void;
  onPrevDate: () => void;
  onNextDate: () => void;
  onToday: () => void;
  isToday: boolean;
  dateLabel: string;
  onOpenChargeModal: () => void;
}

export default function SalesHeader({
  salesCount,
  selectedRange,
  onRangeChange,
  onPrevDate,
  onNextDate,
  onToday,
  isToday,
  dateLabel,
  onOpenChargeModal
}: SalesHeaderProps) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-2">
      {/* Lado izquierdo: Título, Navegación de Fecha y Pastillas de Período */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        {/* Título y Conteo */}
        <div className="flex items-center gap-2">
          <h1 className="text-2xl md:text-[26px] font-bold text-slate-900 tracking-tight">
            Ventas
          </h1>
          <span className="text-slate-400 font-normal text-xl">·</span>
          <span className="text-slate-500 font-medium text-lg">
            {salesCount}
          </span>
        </div>

        {/* Navegador de Fecha < Hoy > */}
        <div className="flex items-center bg-white border border-slate-200/90 rounded-xl px-2 py-1 shadow-sm">
          <button
            onClick={onPrevDate}
            className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition"
            title="Día anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={onToday}
            className="px-3 py-0.5 text-sm font-semibold text-slate-800 hover:text-emerald-800 transition min-w-[70px] text-center"
          >
            {isToday ? 'Hoy' : dateLabel}
          </button>
          <button
            onClick={onNextDate}
            className="p-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition"
            title="Día siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Pastillas de Período [ Día | Semana | Mes | Todo ] */}
        <div className="flex items-center bg-white border border-slate-200/90 p-1 rounded-xl shadow-sm text-xs font-semibold">
          <button
            onClick={() => onRangeChange('day')}
            className={`px-3 py-1 rounded-lg transition ${
              selectedRange === 'day'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Día
          </button>
          <button
            onClick={() => onRangeChange('week')}
            className={`px-3 py-1 rounded-lg transition ${
              selectedRange === 'week'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => onRangeChange('month')}
            className={`px-3 py-1 rounded-lg transition ${
              selectedRange === 'month'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => onRangeChange('all')}
            className={`px-3 py-1 rounded-lg transition ${
              selectedRange === 'all'
                ? 'bg-[#047857] text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            Todo
          </button>
        </div>
      </div>

      {/* Lado derecho: Accesos rápidos y Botón Principal + Cobrar Cliente */}
      <div className="flex items-center gap-2 self-end lg:self-center">
        {/* Accesos rápidos circulares */}
        <button 
          className="w-9 h-9 rounded-full bg-white border border-slate-200/90 text-slate-600 hover:text-emerald-700 hover:border-slate-300 flex items-center justify-center transition shadow-sm"
          title="Bonificaciones y canjes"
        >
          <Coins className="w-4 h-4" />
        </button>
        <button 
          className="w-9 h-9 rounded-full bg-white border border-slate-200/90 text-slate-600 hover:text-emerald-700 hover:border-slate-300 flex items-center justify-center transition shadow-sm"
          title="Calendario"
        >
          <Calendar className="w-4 h-4" />
        </button>
        <button 
          className="w-9 h-9 rounded-full bg-white border border-slate-200/90 text-slate-600 hover:text-emerald-700 hover:border-slate-300 flex items-center justify-center transition shadow-sm"
          title="Clientes"
        >
          <Users className="w-4 h-4" />
        </button>
        <button 
          className="w-9 h-9 rounded-full bg-white border border-slate-200/90 text-slate-600 hover:text-emerald-700 hover:border-slate-300 flex items-center justify-center transition shadow-sm"
          title="Promociones"
        >
          <Tag className="w-4 h-4" />
        </button>

        {/* Botón Verde Circular + para Cobrar Cliente */}
        <button
          onClick={onOpenChargeModal}
          className="w-9 h-9 rounded-full bg-[#047857] hover:bg-[#065f46] text-white flex items-center justify-center shadow-md shadow-emerald-900/20 transition transform active:scale-95"
          title="Cobrar cliente o turno suelto"
        >
          <Plus className="w-5 h-5 stroke-[2.5]" />
        </button>

        {/* Campana de Notificaciones con Badge */}
        <div className="relative pl-1">
          <button className="w-9 h-9 rounded-full bg-white border border-slate-200/90 text-slate-600 hover:text-slate-900 flex items-center justify-center transition shadow-sm">
            <Bell className="w-4 h-4" />
          </button>
          <span className="absolute top-0 right-0 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            3
          </span>
        </div>
      </div>
    </div>
  );
}
